import type {
    ActionEnum,
    PaginatedResponse,
    PartyProduct,
    ProductLookupPriceResult,
    ReviewCreateRequest,
} from './types';

import { baseRequest } from '@/lib/base';

// ── Connections ──
export const fetchConnections = () => baseRequest({
    url: '/system/connections/',
});

export const removeConnection = (id: number) => baseRequest({
    url: `/system/connections/${id}/remove/`,
    method: 'DELETE',
});

// ── Connection Requests ──
export const sendConnectionRequest = (
    toUserId: number,
) => baseRequest({
    url: '/system/connections/send/',
    method: 'POST',
    body: { to_user_id: toUserId },
});

export const fetchReceivedRequests = () => baseRequest({
    url: '/system/connections/pending/received/',
});

export const fetchSentRequests = () => baseRequest({
    url: '/system/connections/pending/sent/',
});

export const respondToRequest = (
    id: number,
    action: ActionEnum,
) => baseRequest({
    url: `/system/connections/${id}/respond/`,
    method: 'PATCH',
    body: { action },
});

export const cancelRequest = (id: number) => baseRequest({
    url: `/system/connections/${id}/cancel/`,
    method: 'DELETE',
});

// ── Search ──
export const searchUsers = (q: string) => baseRequest({
    url: '/system/connections/search/',
    options: { params: { q } },
});

export const searchUsersByPan = (q: string) => baseRequest({
    url: '/system/connections/search/by-pan/',
    options: { params: { q } },
});

// ── Search by Product ──
export const searchUsersByProduct = (q: string) => baseRequest({
    url: '/system/connections/search/by-product/',
    options: { params: { q } },
});

// ── Connected User Detail ──
export const fetchConnectedUser = (
    userId: number,
) => baseRequest({
    url: `/system/connections/${userId}/`,
});

// ── Connected User Products ──
export const fetchConnectedUserProducts = (
    userId: number,
    page = 1,
    pageSize = 10,
    search = '',
) => baseRequest({
    url: `/system/connections/${userId}/products/`,
    options: {
        params: {
            page,
            page_size: pageSize,
            ...(search ? { search } : {}),
        },
    },
});

export const fetchConnectedUserProductBySlug = async (
    userId: number,
    slug: string,
) => {
    const pageSize = 100;

    const fetchPage = async (page: number): Promise<{
        data: PaginatedResponse<PartyProduct> | PartyProduct | null;
        ok: boolean;
        status: number;
        statusText: string;
    }> => {
        if (page > 10) {
            return {
                data: null,
                ok: false,
                status: 404,
                statusText: 'Not Found',
            };
        }

        const response = await fetchConnectedUserProducts(
            userId,
            page,
            pageSize,
        );

        if (!response.ok) return response;

        const data = response.data as PaginatedResponse<PartyProduct>;
        const products = Array.isArray(data.results)
            ? data.results
            : [];
        const match = products.find(
            (product) => product.slug === slug,
        );

        if (match) {
            return {
                ...response,
                data: match,
            };
        }

        if (!data.next || products.length === 0) {
            return {
                data: null,
                ok: false,
                status: 404,
                statusText: 'Not Found',
            };
        }

        return fetchPage(page + 1);
    };

    return fetchPage(1);
};

// Walks the supplier's catalogue so callers can map each product to its
// available units. Capped at 10 pages, matching fetchConnectedUserProductBySlug.
export const fetchAllConnectedUserProducts = async (
    userId: number,
): Promise<PartyProduct[]> => {
    const pageSize = 100;

    const fetchPage = async (
        page: number,
        collected: PartyProduct[],
    ): Promise<PartyProduct[]> => {
        if (page > 10) return collected;

        const response = await fetchConnectedUserProducts(userId, page, pageSize);
        if (!response.ok) return collected;

        const data = response.data as PaginatedResponse<PartyProduct>;
        let products: PartyProduct[] = [];
        if (Array.isArray(data.results)) products = data.results;
        const next = [...collected, ...products];

        if (!data.next || products.length === 0) return next;
        return fetchPage(page + 1, next);
    };

    return fetchPage(1, []);
};

export const lookupProductsForCart = (slugs: string[]) => baseRequest({
    url: '/inventory/quotations/product-lookup/',
    method: 'POST',
    body: { slugs },
}) as Promise<{
    data: {
        results?: ProductLookupPriceResult[];
    } | ProductLookupPriceResult[];
    ok: boolean;
    status: number;
    statusText: string;
}>;

// ── Map Coordinates ──
export const fetchUserCoordinates = () => baseRequest({
    url: '/system/users/coordinates/',
});

// ── Favourites & Blocked ──
export const fetchFavourites = () => baseRequest({
    url: '/system/connections/favourites/',
});

export const toggleFavourite = (
    id: number,
) => baseRequest({
    url: `/system/connections/${id}/favourite/`,
    method: 'POST',
});

export const fetchBlocked = () => baseRequest({
    url: '/system/connections/blocked/',
});

export const toggleBlock = (
    id: number,
) => baseRequest({
    url: `/system/connections/${id}/block/`,
    method: 'POST',
});

// ── Transaction History ──
export const fetchTransactionHistory = (
    otherUserId: number,
    page = 1,
    pageSize = 10,
) => baseRequest({
    url: `/order/orders/history/${otherUserId}/`,
    options: { params: { page, page_size: pageSize } },
});

// ── Reviews ──
export const fetchReviews = (
    userId: number,
    page = 1,
    pageSize = 10,
) => baseRequest({
    url: '/order/reviews/',
    options: {
        params: {
            reviewed_user_id: userId,
            page,
            page_size: pageSize,
        },
    },
});

export const createReview = (
    data: ReviewCreateRequest,
) => baseRequest({
    url: '/order/reviews/',
    method: 'POST',
    body: data,
});
